import styled from "styled-components";
import dayjs from "dayjs";

import { CloseButton } from "components/atoms";

import { MAIN_THEME } from "styles/colors";
import { FONT_FAMILY_BOLD, FONT_FAMILY_REGULAR, FONT_WEIGHT, FONT_WEIGHT_MEDIUM, FONT_WEIGHT_BOLD } from "styles/typography";
import { useCallback, useEffect, useMemo, useRef } from "react";

interface GuestInfo {
	first_name?: string;
	last_name?: string;
	picture_url?: string;
	location?: string;
	phone?: string;
	email?: string;
}

const InfoCategoryContainer = styled.div`
	margin-bottom: 24px;
`;

const Backdrop = styled.button`
	position: fixed;
	inset: 0;
	background: rgba(0, 0, 0, 0.6);
	border: none;
	padding: 0;
	margin: 0;
	cursor: pointer;
	z-index: 1000;

	&:hover {
		background: rgba(0, 0, 0, 0.65);
	}
`;

const InfoRow = styled.div`
	display: grid;
	grid-template-columns: 1fr 2fr;
	gap: 16px;
	margin-bottom: 16px;

	> p:nth-child(2) {
		text-align: right;
	}

	@media (max-width: 480px) {
		grid-template-columns: 1fr 1fr;
		gap: 10px;
		margin-bottom: 12px;
	}
`;

const MainInfo = styled.div`
	display: grid;
	grid-template-columns: repeat(4, minmax(0, 1fr));
	gap: 16px;
	margin-bottom: 24px;
	padding-bottom: 16px;
	border-bottom: 1px solid ${MAIN_THEME.BORDER_COLOR};

	@media (max-width: 768px) {
		grid-template-columns: repeat(4, minmax(0, 1fr));
		gap: 12px;
	}

	@media (max-width: 480px) {
		grid-template-columns: repeat(4, minmax(0, 1fr));
		gap: 8px;
	}
`;


const PropertyContainer = styled.div``;

const InfoTitle = styled.p`
	color: ${MAIN_THEME.TEXT_SECONDARY};
	font-size: 13px;
	font-weight: ${FONT_WEIGHT_MEDIUM};
	font-family: ${FONT_FAMILY_REGULAR};
	letter-spacing: 0.2px;
	margin: 0;

	@media (max-width: 480px) {
		font-size: 12px;
	}
`;

const InfoValue = styled.p`
	color: ${MAIN_THEME.TEXT_PRIMARY};
	font-size: 13px;
	font-weight: ${FONT_WEIGHT_MEDIUM};
	font-family: ${FONT_FAMILY_REGULAR};
	margin: 0;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;

	@media (max-width: 480px) {
		font-size: 12px;
	}
`;

const InfoCategoryTitle = styled.p`
	font-size: 16px;
	color: ${MAIN_THEME.TEXT_PRIMARY};
	margin-bottom: 16px;
	font-weight: ${FONT_WEIGHT_BOLD};
	font-family: ${FONT_FAMILY_REGULAR};
	letter-spacing: 0.3px;
	margin: 0 0 16px 0;

	@media (max-width: 480px) {
		font-size: 14px;
		margin-bottom: 12px;
	}
`;

const InfoColumn = styled.div`
	text-align: center;
	min-width: 0;

	> p:first-child {
		margin-bottom: 8px;
	}
`;

const GuestImageContainer = styled.div`
	display: flex;
	justify-content: center;
	margin-bottom: 16px;
`;

const GuestImage = styled.img`
	width: 120px;
	height: 120px;
	border-radius: 50%;
	object-fit: cover;
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
	border: 3px solid ${MAIN_THEME.BORDER_COLOR};
`;

const GuestName = styled.p`
	font-family: ${FONT_FAMILY_REGULAR};
	font-size: 18px;
	font-weight: ${FONT_WEIGHT_BOLD};
	color: ${MAIN_THEME.TEXT_PRIMARY};
	text-align: center;
	margin: 0 0 14px 0;
`;

const GuestLocation = styled.p`
	font-family: ${FONT_FAMILY_REGULAR};
	font-size: 14px;
	color: ${MAIN_THEME.TEXT_SECONDARY};
	text-align: center;
	margin: 0 0 16px 0;
`;

const TopContainer = styled.div`
	padding-top: 8px;
	margin-bottom: 16px;
	border-bottom: 1px solid ${MAIN_THEME.BORDER_COLOR};
	padding-bottom: 16px;
`;

const DetailsContainer = styled.div`
	display: grid;
	grid-template-columns: 1fr;
	gap: 28px;
	margin-bottom: 28px;
`;

const Container = styled.div`
	background: ${MAIN_THEME.DARK_BG};
	box-shadow: -8px 0 24px rgba(0, 0, 0, 0.5);
	border-left: 1px solid ${MAIN_THEME.BORDER_COLOR};
	height: 100vh;
	width: 490px;
	position: fixed;
	top: 0;
	right: 0;
	padding: 20px 32px;
	z-index: 1001;
	overflow-y: scroll;
	font-family: ${FONT_FAMILY_REGULAR};
	color: ${MAIN_THEME.TEXT_PRIMARY};

	@media (max-width: 768px) {
		width: 100%;
		--mobile-nav-height: 70px;
		padding: 20px 20px calc(var(--mobile-nav-height) + 24px + env(safe-area-inset-bottom)) 20px;
		height: calc(100dvh - var(--mobile-nav-height));
		bottom: var(--mobile-nav-height);
		overflow-y: auto;
		scroll-padding-bottom: calc(var(--mobile-nav-height) + 24px + env(safe-area-inset-bottom));
	}

	@media (max-width: 480px) {
		width: 100%;
		--mobile-nav-height: 60px;
		padding: 15px 15px calc(var(--mobile-nav-height) + 24px + env(safe-area-inset-bottom)) 15px;
		box-shadow: none;
		border-left: none;
		height: calc(100dvh - var(--mobile-nav-height));
		bottom: var(--mobile-nav-height);
		overflow-y: auto;
		scroll-padding-bottom: calc(var(--mobile-nav-height) + 24px + env(safe-area-inset-bottom));
	}
`;

const Title = styled.p`
	font-family: ${FONT_FAMILY_REGULAR};
	text-transform: capitalize;
	font-size: 24px;
	margin: 0 0 20px 0;
	color: ${MAIN_THEME.TEXT_PRIMARY};
	text-align: center;
	font-weight: ${FONT_WEIGHT_BOLD};
	letter-spacing: 0.3px;

	@media (max-width: 768px) {
		font-size: 22px;
		margin-bottom: 15px;
	}

	@media (max-width: 480px) {
		font-size: 20px;
		margin-bottom: 12px;
	}
`;

const Divider = styled.div`
	width: 100%;
	height: 1px;
	background: ${MAIN_THEME.BORDER_COLOR};
	margin: 16px 0;
`;

interface Props {
	close: () => void;
	hostServiceFee: string;
	ownerIncome: string;
	hoteliseHelp: string;
	payoutPrice: string;
	propertyName: string;
	propertyAddress: string;
	checkIn: string;
	checkOut: string;
	nights: string | number;
	guestsNumber: number;
	image: string;
	currency: string;
	cleaningFee?: string;
	guest?: GuestInfo;
}

const ReservationDetailsPanel = (props: Props): JSX.Element => {
	const {
		close,
		hostServiceFee,
		ownerIncome,
		hoteliseHelp,
		payoutPrice,
		propertyName,
		propertyAddress,
		checkIn,
		checkOut,
		nights,
		guestsNumber,
		image,
		currency,
		cleaningFee,
		guest,
	} = props;
	const panelRef = useRef<HTMLDivElement | null>(null);
	const touchStartY = useRef<number | null>(null);
	const touchStartScrollTop = useRef<number>(0);
	const didPushState = useRef(false);
	const isClosing = useRef(false);

	const handleClose = useCallback(
		(syncHistory = true) => {
			if (isClosing.current) return;
			isClosing.current = true;
			close();

			// If we created a history entry for the panel, remove it.
			// Using `go(-1)` is more reliable on Android Chrome than `back()`.
			if (syncHistory && didPushState.current) {
				window.history.go(-1);
			}
		},
		[close]
	);

	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape") {
				handleClose();
			}
		};

		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, [handleClose]);

	useEffect(() => {
		// Add a history entry so device Back can close the panel.
		// We mark the state so we can identify our own entry.
		window.history.pushState(
			{ ...(window.history.state || {}), reservationDetailsOpen: true },
			""
		);
		didPushState.current = true;

		const handlePopState = () => {
			handleClose(false);
		};

		window.addEventListener("popstate", handlePopState);
		return () => {
			window.removeEventListener("popstate", handlePopState);
		};
	}, [handleClose]);

	const renderGuestHeader = () => {
		const guestName = guest?.first_name
			? `${guest.first_name}${guest.last_name ? ` ${guest.last_name}` : ""}`
			: "Guest";

		return (
			<PropertyContainer>
				{guest?.picture_url && (
					<GuestImageContainer>
						<GuestImage src={guest.picture_url} alt={guestName} />
					</GuestImageContainer>
				)}
				<GuestName>{guestName}</GuestName>
				{guest?.location && <GuestLocation>{guest.location}</GuestLocation>}
			</PropertyContainer>
		);
	};

	const rederMainInfo = () => {
		return (
			<MainInfo>
				<InfoColumn>
					<InfoTitle>Check In</InfoTitle>
					<InfoValue>{dayjs(checkIn).format("D MMM, YYYY")}</InfoValue>
				</InfoColumn>
				<InfoColumn>
					<InfoTitle>Check Out</InfoTitle>
					<InfoValue>{dayjs(checkOut).format("D MMM, YYYY")}</InfoValue>
				</InfoColumn>
				<InfoColumn>
					<InfoTitle>Nights</InfoTitle>
					<InfoValue>{nights}</InfoValue>
				</InfoColumn>
				{guestsNumber ? (
					<InfoColumn>
						<InfoTitle>Guests</InfoTitle>
						<InfoValue>{guestsNumber}</InfoValue>
					</InfoColumn>
				) : null}
			</MainInfo>
		);
	};

	const renderPropertyDetails = () => {
		return (
			<InfoCategoryContainer>
				<InfoCategoryTitle>Property Details</InfoCategoryTitle>
				<InfoRow>
					<InfoTitle>Title</InfoTitle>
					<InfoValue>{propertyName}</InfoValue>
				</InfoRow>
				<InfoRow>
					<InfoTitle>Address</InfoTitle>
					<InfoValue>{propertyAddress}</InfoValue>
				</InfoRow>
			</InfoCategoryContainer>
		);
	};

	const currencySymbol = useMemo(() => {
		switch (currency) {
			case "AMD":
				return "֏";
			case "USD":
				return "$";

			default:
				return "";
		}
	}, [currency]);

	const renderPaymentDetails = () => {
		return (
			<InfoCategoryContainer>
				<InfoCategoryTitle>Payment Details</InfoCategoryTitle>
				<InfoRow>
					<InfoTitle>Payout</InfoTitle>
					<InfoValue>
						{currencySymbol}
						{payoutPrice}
					</InfoValue>
				</InfoRow>
				<Divider />
				<InfoRow>
					<InfoTitle>Hotelise Help</InfoTitle>
					<InfoValue>
						{currencySymbol}
						{hoteliseHelp}
					</InfoValue>
				</InfoRow>
				<InfoRow>
					<InfoTitle>Host Service Fee</InfoTitle>
					<InfoValue>
						{currencySymbol}
						{hostServiceFee}
					</InfoValue>
				</InfoRow>
				{cleaningFee && (
					<InfoRow>
						<InfoTitle>Cleaning Fee</InfoTitle>
						<InfoValue>
							{currencySymbol}
							{cleaningFee}
						</InfoValue>
					</InfoRow>
				)}
				<Divider />
				<InfoRow>
					<InfoTitle>Owner Income</InfoTitle>
					<InfoValue>
						{currencySymbol}
						{ownerIncome}
					</InfoValue>
				</InfoRow>
			</InfoCategoryContainer>
		);
	};

	const renderGuest = () => {
		return (
			<InfoCategoryContainer>
				<InfoCategoryTitle>Guest Info</InfoCategoryTitle>
				<InfoRow>
					<InfoTitle>Full Name</InfoTitle>
					<InfoValue>{`${guest?.first_name || ""}${guest?.last_name ? ` ${guest.last_name}` : ""}`}</InfoValue>
				</InfoRow>
				{guest?.location && (
					<InfoRow>
						<InfoTitle>Location</InfoTitle>
						<InfoValue>{guest.location}</InfoValue>
					</InfoRow>
				)}
				{guest?.email && (
					<InfoRow>
						<InfoTitle>Email</InfoTitle>
						<InfoValue>{guest.email}</InfoValue>
					</InfoRow>
				)}
				{guest?.phone && (
					<InfoRow>
						<InfoTitle>Phone</InfoTitle>
						<InfoValue>{guest.phone}</InfoValue>
					</InfoRow>
				)}
			</InfoCategoryContainer>
		);
	};

		return (
			<>
				<Backdrop
					type="button"
					onClick={() => handleClose()}
					aria-label="Close reservation details"
				/>
				<Container
					ref={panelRef}
					onClick={(event) => event.stopPropagation()}
					onTouchStart={(event) => {
						touchStartY.current = event.touches[0]?.clientY ?? null;
						touchStartScrollTop.current = event.currentTarget.scrollTop;
					}}
					onTouchEnd={(event) => {
						const startY = touchStartY.current;
						if (startY === null) return;
						if (touchStartScrollTop.current > 0) {
							touchStartY.current = null;
							return;
						}
						const endY = event.changedTouches[0]?.clientY ?? startY;
						if (endY - startY > 80) {
							handleClose();
						}
						touchStartY.current = null;
					}}
				>
					<TopContainer>
						<Title>Reservation Details</Title>
					</TopContainer>
					{renderGuestHeader()}
					{rederMainInfo()}
					{guest ? renderGuest() : null}
					{renderPropertyDetails()}
					{renderPaymentDetails()}
					<CloseButton onClick={handleClose} />
				</Container>
			</>
		);
};

export default ReservationDetailsPanel;
